import { DeviceCapabilityItem } from './offlineDataTypes';

export function detectDeviceCapabilities(): DeviceCapabilityItem[] {
  const isBrowser = typeof window !== 'undefined';
  const nav = isBrowser ? navigator : ({} as any);

  return [
    {
      id: 'service-worker',
      name: 'Service Worker API',
      category: 'PWA_CORE',
      status: isBrowser && 'serviceWorker' in nav ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'serviceWorker' in nav,
      description: 'Background caching, network proxy, offline shell delivery.',
      purpose: 'Enables deterministic calculations when disconnected.'
    },
    {
      id: 'cache-api',
      name: 'Cache Storage API',
      category: 'STORAGE',
      status: isBrowser && 'caches' in window ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'caches' in window,
      description: 'Programmatic HTTP request-response caching.',
      purpose: 'Stores static assets and verified financial reference datasets.'
    },
    {
      id: 'indexed-db',
      name: 'IndexedDB API',
      category: 'STORAGE',
      status: isBrowser && 'indexedDB' in window ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'indexedDB' in window,
      description: 'Structured client-side database storage.',
      purpose: 'Persists user scenarios, calculation drafts and outbox mutations.'
    },
    {
      id: 'push-api',
      name: 'Push API',
      category: 'COMMUNICATION',
      status: isBrowser && 'PushManager' in window ? 'SUPPORTED' : 'PARTIAL',
      detected: isBrowser && 'PushManager' in window,
      description: 'Receiving messages pushed from server via VAPID protocol.',
      purpose: 'Delivers user-configured FX target alerts and tax regulation alerts.'
    },
    {
      id: 'notifications-api',
      name: 'Notifications API',
      category: 'COMMUNICATION',
      status:
        isBrowser && 'Notification' in window
          ? (Notification.permission as any) === 'granted'
            ? 'SUPPORTED'
            : 'PERMISSION_REQUIRED'
          : 'UNSUPPORTED',
      detected: isBrowser && 'Notification' in window,
      permissionState:
        isBrowser && 'Notification' in window
          ? Notification.permission === 'granted'
            ? 'granted'
            : Notification.permission === 'denied'
            ? 'denied'
            : 'prompt'
          : 'unsupported',
      description: 'System-level banners and lock-screen notification display.',
      purpose: 'Displays urgent financial alerts and calculation completions.'
    },
    {
      id: 'background-sync',
      name: 'Background Sync API',
      category: 'COMMUNICATION',
      status: isBrowser && 'serviceWorker' in nav && 'SyncManager' in window ? 'SUPPORTED' : 'PARTIAL',
      detected: isBrowser && 'SyncManager' in window,
      description: 'Deferred task execution when connectivity is restored.',
      purpose: 'Flushes outbox operations automatically in background.'
    },
    {
      id: 'badging-api',
      name: 'App Badging API',
      category: 'UX',
      status: isBrowser && ('setAppBadge' in nav || 'setExperimentalAppBadge' in nav) ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && ('setAppBadge' in nav || 'setExperimentalAppBadge' in nav),
      description: 'Setting numerical badge count on installed PWA icon.',
      purpose: 'Shows number of unread alerts without intrusive notifications.'
    },
    {
      id: 'web-share',
      name: 'Web Share API',
      category: 'UX',
      status: isBrowser && 'share' in nav ? 'SUPPORTED' : 'PARTIAL',
      detected: isBrowser && 'share' in nav,
      description: 'Native OS share sheet integration for calculators.',
      purpose: 'Allows one-tap sharing of sanitized financial calculations.'
    },
    {
      id: 'clipboard-api',
      name: 'Async Clipboard API',
      category: 'UX',
      status: isBrowser && 'clipboard' in nav ? 'SUPPORTED' : 'PARTIAL',
      detected: isBrowser && 'clipboard' in nav,
      description: 'Fast copying of calculation figures and summary receipts.',
      purpose: 'Empowers users to export results into external spreadsheets.'
    },
    {
      id: 'storage-estimate',
      name: 'StorageManager Quota API',
      category: 'STORAGE',
      status: isBrowser && 'storage' in nav && 'estimate' in nav.storage ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'storage' in nav && 'estimate' in nav.storage,
      description: 'Monitoring remaining disk quota and cache storage usage.',
      purpose: 'Prevents browser storage quota overflow.'
    },
    {
      id: 'screen-orientation',
      name: 'Screen Orientation API',
      category: 'HARDWARE',
      status: isBrowser && 'screen' in window && 'orientation' in screen ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'screen' in window && 'orientation' in screen,
      description: 'Detects portrait vs landscape orientations without locking.',
      purpose: 'Optimizes financial amortization tables for wide phone displays.'
    },
    {
      id: 'network-info',
      name: 'Network Information API',
      category: 'HARDWARE',
      status: isBrowser && 'connection' in nav ? 'SUPPORTED' : 'UNSUPPORTED',
      detected: isBrowser && 'connection' in nav,
      description: 'Detects effective network type (4G, 3G, 2G, save-data).',
      purpose: 'Automatically enables Data Saver mode on slow cellular networks.'
    }
  ];
}
